import React, { useState, useEffect } from 'react';
import { getPayrollSummary, approvePayroll, generatePayrollCSV, getLastPayrollExport, getPayrollDetails, checkPayrollExportStatus } from '../services/payrollService';
import { getPendingDisputes } from '../services/disputeService';
import { getPendingCashTips, getCashTips, getMonthlyCertifications, MonthlyTipCertification } from '../services/cashTipService';
import { getStaff, Staff } from '../services/staffService';
import { getPayrollPeriods } from '../services/payrollPeriodService';
import { PayrollPeriod } from '../types';
import { useFirebase } from '../contexts/FirebaseContext';
import { useStoreLocation } from '../contexts/LocationContext';
import { Calendar, Download, CheckCircle, AlertCircle, ArrowRight, TrendingUp, TrendingDown, Users, DollarSign, ShieldAlert, FileText, BarChart3, Clock, AlertTriangle, ShieldCheck, MapPin } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { PayrollDueReminder } from './PayrollDueReminder';
import { PayrollPeriodManager } from './PayrollPeriodManager';
import { getIRSCashTipDeadline, formatLongDate } from '../lib/complianceUtils';
import { ReconciliationLog } from './ReconciliationLog';
import Papa from 'papaparse';

interface Props {
  tenantId: string;
}

export const PayrollExportForm: React.FC<Props> = ({ tenantId }) => {
  const { user, tenantId: currentTenantId } = useFirebase();
  const { activeLocationId, activeLocation, isConsolidated, availableLocations, changeLocation } = useStoreLocation();
  const [summary, setSummary] = useState({ total: '0.00', count: 0 });
  const [details, setDetails] = useState<any[]>([]);
  const [lastExportDetails, setLastExportDetails] = useState<any[]>([]);
  const [disputeSummary, setDisputeSummary] = useState({ total: 0, count: 0 });
  const [cashTipSummary, setCashTipSummary] = useState({ total: 0, count: 0 });
  const [lastExport, setLastExport] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isPeriodExported, setIsPeriodExported] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [status, setStatus] = useState('');
  const [activeTab, setActiveTab] = useState('export');
  const [exportHistory, setExportHistory] = useState<any[]>([]);
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);
  const [payrollProvider, setPayrollProvider] = useState<string>('');
  const [payrollPeriods, setPayrollPeriods] = useState<PayrollPeriod[]>([]);
  const [expandedComplianceEmp, setExpandedComplianceEmp] = useState<string | null>(null);

  // IRS 10th-Day Compliance State
  const [allCashTips, setAllCashTips] = useState<any[]>([]);
  const [allMasterTxs, setAllMasterTxs] = useState<any[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [allCertifications, setAllCertifications] = useState<MonthlyTipCertification[]>([]);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceYear, setComplianceYear] = useState(new Date().getFullYear());
  const [complianceMonth, setComplianceMonth] = useState(
    new Date().getMonth() - 1 < 0 ? 11 : new Date().getMonth() - 1
  );

  useEffect(() => {
    if (activeTab === 'compliance') {
      const loadComplianceData = async () => {
        setComplianceLoading(true);
        try {
          const [tips, staff, masterTxs, certs] = await Promise.all([
            getCashTips(tenantId),
            getStaff(tenantId),
            import('../services/allocationService').then(m => m.getMasterTransactions(tenantId)),
            getMonthlyCertifications(tenantId, complianceYear, complianceMonth)
          ]);
          setAllCashTips(tips);
          setAllStaff(staff);
          setAllMasterTxs(masterTxs);
          setAllCertifications(certs || []);
        } catch (error) {
          console.error('Error loading compliance data:', error);
        } finally {
          setComplianceLoading(false);
        }
      };
      loadComplianceData();
    }
  }, [activeTab, tenantId, complianceYear, complianceMonth]);

  const getComplianceRecords = () => {
    const year = complianceYear;
    const month = complianceMonth; // 0-indexed
    const deadline = getIRSCashTipDeadline(year, month);
    
    const monthTips = allCashTips.filter(tip => {
      if (!tip.tipDate) return false;
      const parts = tip.tipDate.split('-');
      if (parts.length !== 3) return false;
      const tYear = parseInt(parts[0], 10);
      const tMonth = parseInt(parts[1], 10) - 1;
      return tYear === year && tMonth === month;
    });

    const records: Record<string, {
      name: string;
      email: string;
      posId: string;
      totalAmount: number;
      lastSubmissionDate: Date | null;
      isLate: boolean;
      submissionsCount: number;
      certification: MonthlyTipCertification | null;
      tips: any[];
      submitterEmails: string[];
    }> = {};

    allStaff.forEach(s => {
      if (s.email) {
        const cert = allCertifications.find(c => c.email.toLowerCase() === s.email!.toLowerCase());
        records[s.email] = {
          name: s.name,
          email: s.email,
          posId: s.posId || 'N/A',
          totalAmount: 0,
          lastSubmissionDate: null,
          isLate: false,
          submissionsCount: 0,
          certification: cert || null,
          tips: [],
          submitterEmails: []
        };
      }
    });

    monthTips.forEach(tip => {
      const email = tip.email;
      if (!email) return;

      if (!records[email]) {
        const cert = allCertifications.find(c => c.email.toLowerCase() === email.toLowerCase());
        records[email] = {
          name: tip.staffName || 'Unknown',
          email: email,
          posId: 'N/A',
          totalAmount: 0,
          lastSubmissionDate: null,
          isLate: false,
          submissionsCount: 0,
          certification: cert || null,
          tips: [],
          submitterEmails: []
        };
      }

      const rec = records[email];
      rec.totalAmount += tip.amount || 0;
      rec.submissionsCount += 1;
      rec.tips.push(tip);

      const submitter = tip.submittedBy || tip.email;
      if (submitter && !rec.submitterEmails.includes(submitter)) {
        rec.submitterEmails.push(submitter);
      }

      const masterTx = allMasterTxs.find(m => m.relatedTransactionId === tip.id || m.id === tip.masterTxId);
      let submissionDate = new Date(tip.tipDate + 'T00:00:00');
      if (masterTx?.timestamp && typeof masterTx.timestamp.toDate === 'function') {
        submissionDate = masterTx.timestamp.toDate();
      }

      if (!rec.lastSubmissionDate || submissionDate > rec.lastSubmissionDate) {
        rec.lastSubmissionDate = submissionDate;
      }

      const subReset = new Date(submissionDate.getFullYear(), submissionDate.getMonth(), submissionDate.getDate());
      const deadlineReset = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
      if (subReset > deadlineReset) {
        rec.isLate = true;
      }
    });

    return Object.values(records);
  };

  const complianceRecords = getComplianceRecords();

  const handleExportComplianceCSV = () => {
    const year = complianceYear;
    const month = complianceMonth;
    const monthName = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long' });
    const deadline = getIRSCashTipDeadline(year, month);
    const deadlineStr = formatLongDate(deadline);

    const csvData = complianceRecords.map(r => {
      const status = r.totalAmount === 0 
        ? 'No Tips Reported' 
        : (r.isLate ? 'Late Report (Past Deadline)' : 'In-Compliance');
        
      const lastSubStr = r.lastSubmissionDate 
        ? r.lastSubmissionDate.toLocaleDateString() 
        : 'N/A';

      const submittersStr = r.submitterEmails && r.submitterEmails.length > 0 
        ? r.submitterEmails.join(', ') 
        : r.email;

      return {
        'Employee Name': r.name,
        'Employee Email': r.email,
        'Created/Submitted By Email': submittersStr,
        'POS ID': r.posId,
        'Reporting Month': `${monthName} ${year}`,
        'Total Cash Tips Reported ($)': r.totalAmount.toFixed(2),
        'IRS Reporting Deadline': deadlineStr,
        'Last Submission Date': lastSubStr,
        'Submission Count': r.submissionsCount,
        'IRS Compliance Status': status,
        'Compliance Warning': r.isLate ? 'IRS penalties may apply if late without reasonable cause' : ''
      };
    });

    const grandTotal = complianceRecords.reduce((sum, r) => sum + r.totalAmount, 0);
    csvData.push({
      'Employee Name': 'GRAND TOTAL',
      'Employee Email': '',
      'Created/Submitted By Email': '',
      'POS ID': '',
      'Reporting Month': '',
      'Total Cash Tips Reported ($)': grandTotal.toFixed(2),
      'IRS Reporting Deadline': '',
      'Last Submission Date': '',
      'Submission Count': complianceRecords.reduce((sum, r) => sum + r.submissionsCount, 0),
      'IRS Compliance Status': '',
      'Compliance Warning': ''
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IRS_Cash_Tip_Compliance_Report_${monthName}_${year}.csv`;
    a.click();
  };

  const fetchHistory = async () => {
    try {
      const recordsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
      const q = query(recordsRef, where('tenantId', '==', tenantId), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      let list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (activeLocationId && activeLocationId !== 'all') {
        list = list.filter((r: any) => r.locationId === activeLocationId || (!r.locationId && (activeLocationId === 'loc-downtown' || activeLocationId === tenantId)));
      }
      setExportHistory(list);
    } catch (error) {
      console.error('Error fetching export history:', error);
      // Try without orderBy if it fails due to missing index
      try {
        const recordsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
        const q = query(recordsRef, where('tenantId', '==', tenantId));
        const snapshot = await getDocs(q);
        let list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (activeLocationId && activeLocationId !== 'all') {
          list = list.filter((r: any) => r.locationId === activeLocationId || (!r.locationId && (activeLocationId === 'loc-downtown' || activeLocationId === tenantId)));
        }
        setExportHistory(list);
      } catch (error2) {
        console.error('Error fetching export history without orderBy:', error2);
      }
    }
  };

  useEffect(() => {
    const fetchLast = async () => {
      const [last, pendingDisputes, pendingTips, settingsSnapshot, periodsData] = await Promise.all([
        getLastPayrollExport(tenantId, activeLocationId),
        getPendingDisputes(tenantId),
        getPendingCashTips(tenantId),
        getDoc(doc(db, 'tenants', tenantId, 'settings', 'config')),
        getPayrollPeriods(tenantId)
      ]);
      setLastExport(last);
      setDisputeSummary({
        total: pendingDisputes.reduce((acc, d) => acc + (d.originalAmount - d.correctedAmount), 0),
        count: pendingDisputes.length
      });
      setCashTipSummary({
        total: pendingTips.reduce((acc, t) => acc + t.amount, 0),
        count: pendingTips.length
      });
      setPayrollPeriods(periodsData || []);
      
      if (settingsSnapshot.exists()) {
        setPayrollProvider(settingsSnapshot.data().payrollProvider || 'Standard');
      }
    };
    fetchLast();
    fetchHistory();
  }, [tenantId, activeLocationId]);


  useEffect(() => {
    const checkExport = async () => {
      if (startDate && endDate) {
        const exported = await checkPayrollExportStatus(tenantId, startDate, endDate, activeLocationId);
        setIsPeriodExported(exported);
      }
    };
    checkExport();
  }, [tenantId, startDate, endDate, activeLocationId]);

  const handleReview = async () => {
    if (!startDate || !endDate) {
      setStatus('Please select a date range.');
      return;
    }
    const [data, detailData] = await Promise.all([
      getPayrollSummary(tenantId, startDate, endDate, activeLocationId),
      getPayrollDetails(tenantId, startDate, endDate, activeLocationId)
    ]);

    // Fetch details for the last export period for flux comparison
    if (lastExport?.startDate && lastExport?.endDate) {
      const lastDetails = await getPayrollDetails(tenantId, lastExport.startDate, lastExport.endDate, activeLocationId);
      setLastExportDetails(lastDetails);
    } else {
      setLastExportDetails([]);
    }

    setSummary(data);
    setDetails(detailData);
    setIsReviewing(true);
    setStatus('');
  };

  const handleConfirm = async () => {
    console.log('Confirming export, tenantId:', tenantId, 'user email:', user?.email, 'location:', activeLocationId);
    if (!user?.email || !startDate || !endDate) return;
    setIsExporting(true);
    try {
      await approvePayroll(tenantId, startDate, endDate, user.email, activeLocationId, activeLocation.name);
      setStatus('Tip Payroll Extract Approved! Downloading export...');
      await handleDownload();
      await fetchHistory(); // Refresh history
      setIsReviewing(false);
    } catch (error) {
      console.error('Export failed:', error);
      setStatus('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = async () => {
    const csv = await generatePayrollCSV(tenantId, startDate, endDate, activeLocationId);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const locSlug = activeLocationId === 'all' ? 'All_Locations' : (activeLocation.name || activeLocationId).replace(/\s+/g, '_');
    a.download = `Payroll_${locSlug}_${startDate}_to_${endDate}.csv`;
    a.click();
  };

  const handleReDownload = async (record: any) => {
    let start = record.startDate;
    let end = record.endDate;
    
    if (!start || !end) {
      if (record.period && record.period.includes(' to ')) {
        const parts = record.period.split(' to ');
        start = parts[0]?.trim();
        end = parts[1]?.trim();
      }
    }
    
    if (!start || !end) {
      console.warn('Could not determine date range for re-downloading export:', record);
      return;
    }

    const csv = await generatePayrollCSV(tenantId, start, end, record.locationId || activeLocationId);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Payroll_RE_EXPORT_${record.period?.replace(/ /g, '_') || 'export'}.csv`;
    a.click();
  };

  const tabs = [
    { id: 'export', label: 'Export' },
    { id: 'history', label: 'Export History' },
    { id: 'reconciliation-log', label: 'Reconciliation Log' },
    { id: 'status', label: 'Payroll Period Status' },
    { id: 'compliance', label: 'IRS 10th-Day Compliance' }
  ];

  const hasSelectedDates = startDate && endDate;
  const matchingPeriod = hasSelectedDates
    ? payrollPeriods.find(p => p.startDate === startDate && p.endDate === endDate)
    : null;
  const priorOpenPeriod = startDate
    ? payrollPeriods.find(p => p.endDate < startDate && p.status === 'Open')
    : null;

  if (isReviewing) {
    const totalNum = parseFloat(summary.total);
    const lastTotalNum = lastExport ? parseFloat(lastExport.total) : 0;
    const variance = totalNum - lastTotalNum;
    const variancePercent = lastTotalNum > 0 ? (variance / lastTotalNum) * 100 : 0;

    const toggleExpand = (email: string) => {
      setExpandedEmployee(expandedEmployee === email ? null : email);
    };

    // Prepare chart data from history (most recent 6 exports)
    const chartData = exportHistory
      .slice(0, 5)
      .reverse()
      .map(record => ({
        date: record.dateApproved || record.timestamp?.toDate?.().toLocaleDateString() || 'N/A',
        total: parseFloat(record.totalTips || '0'),
        type: 'Historical'
      }));

    // Add current period to chart data for comparison
    chartData.push({
      date: 'Current',
      total: totalNum,
      type: 'Current'
    });

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">Review Tip Payroll Extract</h2>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                <MapPin size={12} className="text-blue-500" />
                {activeLocation.name}
              </span>
            </div>
            <p className="text-gray-500 mt-1">Period: {startDate} to {endDate}</p>
          </div>
          <button onClick={() => setIsReviewing(false)} className="text-sm font-bold text-gray-500 hover:text-gray-700 cursor-pointer">Back to Selection</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card border-blue-100 bg-blue-50/20 shadow-xl shadow-blue-500/5 transition-all duration-300">
            <div className="p-6 border-b border-blue-50 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp size={20} className="text-blue-600" />
                <h3 className="font-black text-gray-900 tracking-tight uppercase text-sm">Flux Analysis Engine</h3>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-500 bg-blue-100 px-2 py-0.5 rounded italic">Period-over-Period Variance</span>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Volume Shift */}
              <div className="space-y-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <DollarSign size={12} /> Total Distribution Flux
                </p>
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <p className="text-2xl font-black text-gray-900 tracking-tight">${totalNum.toFixed(2)}</p>
                    <p className={`text-xs font-bold ${variance >= 0 ? 'text-emerald-600' : 'text-rose-600'} flex items-center gap-0.5 px-2 py-0.5 rounded-full ${variance >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                      {variance >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
                      {Math.abs(variancePercent).toFixed(1)}%
                    </p>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-1000 ${variance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}
                      style={{ width: `${Math.min(100, Math.abs(variancePercent))}%` }}
                    ></div>
                  </div>
                  <p className="text-[10px] text-gray-400 font-medium italic">Net {variance >= 0 ? 'increase' : 'decrease'} of ${Math.abs(variance).toFixed(2)} vs previous period</p>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    <Users size={12} /> Workforce Participation
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                      <p className="text-2xl font-black text-gray-900">{summary.count}</p>
                      <p className="text-[8px] text-gray-400 font-black uppercase">Active</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 rounded-lg">
                        <span className="text-xs font-black text-emerald-700">
                          +{details.filter(d => !lastExportDetails.find(l => l.email === d.email)).length}
                        </span>
                        <span className="text-[8px] font-bold text-emerald-600 uppercase">New</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-lg">
                        <span className="text-xs font-black text-amber-700">
                          -{lastExportDetails.filter(l => !details.find(d => d.email === l.email)).length}
                        </span>
                        <span className="text-[8px] font-bold text-amber-600 uppercase">Dormant</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Critical Variances */}
              <div className="space-y-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <AlertCircle size={12} /> Variance Monitoring
                </p>
                <div className="space-y-2">
                  {details.map(d => {
                    const last = lastExportDetails.find(l => l.email === d.email);
                    if (!last) return null;
                    const v = d.amount - last.amount;
                    const vp = last.amount > 0 ? (v / last.amount) * 100 : 0;
                    if (Math.abs(vp) < 25) return null;
                    
                    return (
                      <div key={d.email} className="flex items-center justify-between p-2.5 rounded-xl bg-white border border-gray-100 shadow-sm hover:border-blue-200 transition-all">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-black text-gray-900 truncate max-w-[120px]">{d.name}</span>
                          <span className="text-[8px] text-gray-400 font-bold uppercase tracking-tighter">Significant Shift</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                           <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${vp > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                            {vp > 0 ? '+' : ''}{vp.toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    );
                  }).filter(Boolean).slice(0, 2)}
                  
                  {details.every(d => {
                    const last = lastExportDetails.find(l => l.email === d.email);
                    return !last || Math.abs((d.amount - last.amount) / (last.amount || 1) * 100) < 25;
                  }) && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 border-dashed">
                      <CheckCircle size={14} className="text-emerald-500" />
                      <span className="text-[10px] font-bold text-emerald-700 italic">No outliers detected in staff distribution</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Historical Comparison Chart */}
          <div className="card p-6 bg-white border border-gray-100 flex flex-col">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 size={18} className="text-blue-600" />
              <h3 className="font-bold text-gray-900 text-sm">Export Velocity</h3>
            </div>
            <div className="flex-grow min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 9, fill: '#9ca3af', fontWeight: 600 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 9, fill: '#9ca3af', fontWeight: 600 }}
                    tickFormatter={(val) => `$${val}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}
                    prefixStyle={{ display: 'none' }}
                  />
                  <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.type === 'Current' ? '#2563eb' : '#e2e8f0'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-4 text-[10px] text-gray-400 text-center font-medium">Historical export totals vs. current period</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6 bg-white border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-rose-50 rounded-lg text-rose-600">
                <ShieldAlert size={20} />
              </div>
              <h3 className="font-bold text-gray-900">Open Disputes</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">{disputeSummary.count}</p>
            <p className="text-xs text-gray-500 mt-2">${disputeSummary.total.toFixed(2)} impact</p>
          </div>

          <div className="card p-6 bg-white border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                <FileText size={20} />
              </div>
              <h3 className="font-bold text-gray-900">Pending Cash Tips</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">{cashTipSummary.count}</p>
            <p className="text-xs text-gray-500 mt-2">${cashTipSummary.total.toFixed(2)} to approve</p>
          </div>

          <div className="card p-6 bg-white border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                <Calendar size={20} />
              </div>
              <h3 className="font-bold text-gray-900">Last Export</h3>
            </div>
            <p className="text-lg font-bold text-gray-900">{lastExport ? lastExport.dateApproved : 'None'}</p>
            <p className="text-xs text-gray-500 mt-2">{lastExport ? `$${lastExport.total || '0.00'} for ${lastExport.count || 0} staff` : 'First time export'}</p>
          </div>
        </div>
        
        <div className="card overflow-hidden">
          <div className="p-6 border-b border-gray-50 bg-gray-50/30">
            <h3 className="font-bold text-gray-900">Detailed Breakdown</h3>
          </div>
          
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Employee Name</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Location</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">POSId</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Email</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Period Start</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Period End</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Pay Method</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-nowrap">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right text-nowrap">Total Tips</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {details.map((s, i) => (
                  <React.Fragment key={i}>
                    <tr 
                      className="hover:bg-blue-50/30 transition-colors cursor-pointer group"
                      onClick={() => toggleExpand(s.email)}
                    >
                      <td className="px-6 py-4 font-semibold text-gray-900 text-sm flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full bg-blue-600 transition-all ${expandedEmployee === s.email ? 'scale-150 opacity-100' : 'scale-0 opacity-0'}`} />
                        {s.name}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[11px] font-medium">
                          <MapPin size={10} className="text-gray-400" />
                          {s.locationName || activeLocation.name || 'Primary'}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-gray-600">{s.posId}</td>
                      <td className="px-6 py-4 text-xs text-gray-600">{s.email}</td>
                      <td className="px-6 py-4 text-xs text-gray-600">{s.startDate}</td>
                      <td className="px-8 py-4 text-xs text-gray-600">{s.endDate}</td>
                      <td className="px-6 py-4 text-xs text-gray-600">{s.paymentMethod}</td>
                      <td className="px-6 py-4 text-[10px] font-black uppercase text-blue-600 tracking-widest opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {expandedEmployee === s.email ? 'Click to collapse' : 'Click to drill down'}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-gray-900">${s.amount.toFixed(2)}</td>
                    </tr>
                    {expandedEmployee === s.email && (
                      <tr>
                        <td colSpan={9} className="px-6 py-0 bg-blue-50/10">
                          <div className="py-6 border-l-4 border-blue-600 ml-2 pl-6 animate-in slide-in-from-top-2 duration-300">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                              Incoming Transaction Stream • {s.name}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {s.transactions?.map((tx: any) => (
                                <div key={tx.id} className="p-4 bg-white rounded-2xl border border-blue-100 shadow-sm flex flex-col gap-2">
                                  <div className="flex justify-between items-start">
                                    <div className="space-y-1">
                                      <p className="text-sm font-bold text-gray-900">${tx.amount.toFixed(2)}</p>
                                      <p className="text-[8px] font-black text-blue-600 uppercase tracking-widest">{tx.type}</p>
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-400">{tx.tipDate}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-500 font-bold uppercase tracking-tight bg-gray-50 px-2 py-1 rounded">
                                    <Clock size={10} />
                                    <span>{tx.shift} Shift</span>
                                  </div>
                                  <div className="mt-1 pt-2 border-t border-gray-50 flex flex-col gap-1">
                                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                                      <CheckCircle size={10} className="text-emerald-500" />
                                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Verified by</span>
                                      <span className="text-[9px] font-bold text-gray-600 truncate max-w-[100px]">{tx.approvedBy}</span>
                                    </div>
                                    <div className="text-[8px] text-gray-300 font-medium tabular-nums ml-4">
                                      {tx.approvedAt}
                                    </div>
                                  </div>
                                  {tx.note && (
                                    <p className="text-[10px] text-gray-400 italic font-medium truncate">"{tx.note}"</p>
                                  )}
                                </div>
                              ))}
                            </div>
                            {(!s.transactions || s.transactions.length === 0) && (
                              <p className="text-sm text-gray-400 italic">No detailed transactions found for this period.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-right font-bold text-gray-900 text-sm">Total</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">${details.reduce((acc, s) => acc + s.amount, 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-gray-50">
            {details.map((s, i) => (
              <div key={i} className="space-y-0">
                <div 
                  className={`p-4 space-y-3 cursor-pointer transition-colors ${expandedEmployee === s.email ? 'bg-blue-50/50' : 'active:bg-gray-50'}`}
                  onClick={() => toggleExpand(s.email)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-gray-900">{s.name}</p>
                      <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">{s.paymentMethod} • POS: {s.posId}</p>
                      <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">
                        <MapPin size={9} />
                        {s.locationName || activeLocation.name || 'Primary'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-black text-blue-600">${s.amount.toFixed(2)}</p>
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[8px] font-black uppercase border border-emerald-100">
                        {s.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500 font-medium">
                    <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                      <Calendar size={10} />
                      <span>{s.startDate} — {s.endDate}</span>
                    </div>
                    <span className="text-blue-600 font-black uppercase tracking-widest text-[8px]">
                      {expandedEmployee === s.email ? 'Close Details' : 'View Transactions'}
                    </span>
                  </div>
                </div>
                
                {expandedEmployee === s.email && (
                  <div className="p-4 bg-blue-50/10 border-y border-blue-50 space-y-4 animate-in slide-in-from-top-2 duration-300">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 px-2">
                       Transaction Stream
                    </h4>
                    <div className="space-y-3">
                      {s.transactions?.map((tx: any) => (
                        <div key={tx.id} className="p-4 bg-white rounded-2xl border border-blue-100 shadow-sm space-y-2">
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-bold text-gray-900">${tx.amount.toFixed(2)}</p>
                            <span className="text-[10px] font-medium text-gray-400">{tx.tipDate}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-[8px] font-black text-blue-600 uppercase tracking-widest">{tx.type}</p>
                            <div className="flex items-center gap-1.5 text-[8px] font-black text-gray-400 uppercase">
                              <Clock size={8} />
                              <span>{tx.shift}</span>
                            </div>
                          </div>
                          <div className="pt-2 border-t border-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              <CheckCircle size={8} className="text-emerald-500" />
                              <span className="text-[8px] font-black text-gray-400 uppercase">By {tx.approvedBy}</span>
                            </div>
                            <span className="text-[7px] text-gray-300 tabular-nums">{tx.approvedAt}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-end space-x-4 pt-4">
          <button 
            onClick={() => handleDownload()}
            className="px-6 py-3 rounded-xl font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all flex items-center gap-2"
          >
            <FileText size={18} />
            Download Draft CSV
          </button>
          <button 
            onClick={() => setIsReviewing(false)}
            className="px-6 py-3 rounded-xl font-bold text-gray-600 hover:bg-gray-100 transition-all"
          >
            Cancel
          </button>
          <button 
            onClick={handleConfirm}
            disabled={isExporting}
            className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 transition-all flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Processing...
              </>
            ) : (
              <>
                <Download size={18} />
                <span>Download {payrollProvider || ''} Tip Export</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PayrollDueReminder />
      <div className="flex items-center space-x-8 border-b border-gray-200 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-4 text-xs font-black uppercase tracking-widest transition-all relative whitespace-nowrap ${
              activeTab === tab.id 
                ? 'text-blue-600' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
            )}
          </button>
        ))}
        <div className="min-w-[2rem] md:hidden" />
      </div>

      {activeTab === 'export' ? (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Tip Payroll Extract</h2>
            <p className="text-gray-500 mt-2 text-lg">Select a pay period to generate your tip distribution report.</p>
            
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              {payrollProvider && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Target Format:</span>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-black uppercase italic shadow-sm border border-blue-100 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                    {payrollProvider}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Store Scope:</span>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-800 rounded-full text-xs font-bold shadow-sm border border-emerald-100 flex items-center gap-1.5">
                  <MapPin size={12} className="text-emerald-600" />
                  {activeLocation.name}
                </span>
              </div>
            </div>
          </div>

          <div className="card p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={e => setStartDate(e.target.value)} 
                    className="w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium text-sm sm:text-base min-h-[3rem]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={e => setEndDate(e.target.value)} 
                    className="w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium text-sm sm:text-base min-h-[3rem]"
                  />
                </div>
              </div>
            </div>

            {/* Pay Period Status Reminders */}
            {hasSelectedDates && (
              <div className="space-y-3">
                {!matchingPeriod && (
                  <div className="p-4 rounded-xl text-xs font-medium flex items-start gap-3 bg-amber-50 text-amber-800 border border-amber-200">
                    <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-extrabold uppercase tracking-widest text-[9px] text-amber-700">Set Pay Period First</p>
                      <p className="mt-1">Please set the pay period for <strong>{startDate} to {endDate}</strong> under the <span className="font-bold">Payroll Period Status</span> tab before running the payroll export.</p>
                    </div>
                  </div>
                )}

                {matchingPeriod && isPeriodExported && (
                  <div className="p-4 rounded-xl text-xs font-medium flex items-start gap-3 bg-blue-50 text-blue-800 border border-blue-200">
                    <CheckCircle size={16} className="text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-extrabold uppercase tracking-widest text-[9px] text-blue-700">Export Completed</p>
                      <p className="mt-1">You have already run the payroll export for this pay period ({startDate} to {endDate}).</p>
                    </div>
                  </div>
                )}

                {priorOpenPeriod && (
                  <div className="p-4 rounded-xl text-xs font-medium flex items-start gap-3 bg-rose-50 text-rose-800 border border-rose-200">
                    <AlertTriangle size={16} className="text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-extrabold uppercase tracking-widest text-[9px] text-rose-700">Close Prior Periods</p>
                      <p className="mt-1">Please close prior payroll periods (such as <strong>{priorOpenPeriod.startDate} to {priorOpenPeriod.endDate}</strong>) before generating a new payroll export.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button 
              onClick={handleReview}
              disabled={!startDate || !endDate}
              className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all flex items-center justify-center gap-2 group"
            >
              <span>Review Tip Payroll Extract Data</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {status && (
              <div className={`p-4 rounded-xl text-center text-sm font-bold flex items-center justify-center gap-2 ${
                status.includes('Approved') ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
              }`}>
                {status.includes('Approved') ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {status}
              </div>
            )}
          </div>

          {lastExport && (
            <div className="p-6 bg-gray-50/50 border border-gray-100 rounded-2xl flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Download className="text-gray-400" size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Last Exported</p>
                  <p className="text-sm font-bold text-gray-900">{lastExport.dateApproved}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Amount</p>
                <p className="text-sm font-bold text-gray-900">${lastExport.total}</p>
              </div>
            </div>
          )}
        </div>
      ) : activeTab === 'history' ? (
        <div className="card overflow-hidden">
          <div className="hidden md:block">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Timestamp</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Location</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Period</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Approver</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Total Tips</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Paycheck Tips</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {exportHistory.map((record) => (
                  <tr key={record.id}>
                    <td className="px-8 py-4 text-sm font-medium">{record.timestamp?.toDate ? record.timestamp.toDate().toLocaleString() : 'Pending...'}</td>
                    <td className="px-8 py-4 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                        <MapPin size={11} className="text-gray-400" />
                        {record.locationName || (record.locationId === 'all' ? 'All Locations' : 'Primary')}
                      </span>
                    </td>
                    <td className="px-8 py-4 text-sm text-gray-600">{record.period}</td>
                    <td className="px-8 py-4 text-sm text-gray-600">{record.approvedBy || 'N/A'}</td>
                    <td className="px-8 py-4 text-sm text-gray-600">{record.totalTips ? `$${record.totalTips}` : 'N/A'}</td>
                    <td className="px-8 py-4 text-sm text-gray-600">{record.paycheckTips || 'N/A'}</td>
                    <td className="px-8 py-4"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">{record.status}</span></td>
                    <td className="px-8 py-4 text-right"><button onClick={() => handleReDownload(record)} className="text-blue-600 hover:text-blue-800 font-bold text-xs uppercase">Re-Download</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-gray-50">
            {exportHistory.map((record) => (
              <div key={record.id} className="p-4 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-gray-900">
                      {record.timestamp?.toDate ? record.timestamp.toDate().toLocaleDateString() : 'Pending...'}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">{record.period}</p>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">
                      <MapPin size={9} />
                      {record.locationName || (record.locationId === 'all' ? 'All Locations' : 'Primary')}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-black uppercase">
                    {record.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded-xl">
                  <div>
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Total Tips</p>
                    <p className="text-xs font-bold text-gray-900">{record.totalTips ? `$${record.totalTips}` : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Paycheck</p>
                    <p className="text-xs font-bold text-gray-900">{record.paycheckTips || 'N/A'}</p>
                  </div>
                  <div className="col-span-2 border-t border-gray-100 pt-2">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Approved By</p>
                    <p className="text-xs font-medium text-gray-600 truncate">{record.approvedBy || 'N/A'}</p>
                  </div>
                </div>

                <button 
                  onClick={() => handleReDownload(record)}
                  className="w-full py-2.5 bg-white border border-gray-100 rounded-lg text-blue-600 font-black text-[10px] uppercase tracking-widest shadow-sm flex items-center justify-center gap-2"
                >
                  <Download size={14} />
                  Re-Download CSV
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : activeTab === 'compliance' ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="card p-6 md:p-8 space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase italic">IRS 10th-Day Tip Compliance</h3>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Audit employee cash tip reporting timelines against IRS mandates</p>
              </div>
              
              <button 
                onClick={handleExportComplianceCSV}
                disabled={complianceLoading || complianceRecords.length === 0}
                className="w-full md:w-auto h-12 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-500/10 transition-all cursor-pointer"
              >
                <Download size={16} />
                <span>One-Click Export (CSV)</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Tax Year</label>
                <select 
                  value={complianceYear} 
                  onChange={e => setComplianceYear(parseInt(e.target.value, 10))}
                  className="w-full h-12 px-3 bg-gray-50 border border-gray-100 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                  <option value={2027}>2027</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Reporting Month</label>
                <select 
                  value={complianceMonth} 
                  onChange={e => setComplianceMonth(parseInt(e.target.value, 10))}
                  className="w-full h-12 px-3 bg-gray-50 border border-gray-100 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Array.from({ length: 12 }).map((_, i) => (
                    <option key={i} value={i}>
                      {new Date(2026, i, 1).toLocaleDateString('en-US', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                  <Calendar size={18} />
                </div>
                <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Official IRS Deadline</p>
                  <p className="text-xs font-black text-gray-900 mt-0.5">
                    {formatLongDate(getIRSCashTipDeadline(complianceYear, complianceMonth))}
                  </p>
                </div>
              </div>
            </div>

            {complianceLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Employee Name</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Account & Submitter Email</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">POS ID</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Cash Tips Reported</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Last Submission</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Employee Sign-off</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(() => {
                      const complianceDeadline = getIRSCashTipDeadline(complianceYear, complianceMonth);
                      return complianceRecords.map((r, i) => {
                        const hasTips = r.totalAmount > 0;
                        const statusColor = !hasTips 
                          ? 'bg-gray-100 text-gray-500 border-gray-200' 
                          : (r.isLate ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200');
                          
                        const statusLabel = !hasTips 
                          ? 'No Tips Reported' 
                          : (r.isLate ? 'Late Report' : 'In-Compliance');

                        const isExpanded = expandedComplianceEmp === r.email;

                        return (
                          <React.Fragment key={i}>
                            <tr 
                              className={`hover:bg-blue-50/30 transition-colors cursor-pointer group ${isExpanded ? 'bg-blue-50/20' : ''}`}
                              onClick={() => setExpandedComplianceEmp(isExpanded ? null : r.email)}
                            >
                              <td className="px-6 py-4 font-semibold text-gray-900 text-sm">
                                <div className="flex items-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-full bg-blue-600 transition-all ${isExpanded ? 'scale-150 opacity-100' : 'scale-0 opacity-0'}`} />
                                  <div>
                                    <p className="font-bold text-gray-900">{r.name}</p>
                                    {r.tips && r.tips.length > 0 && (
                                      <p className="text-[10px] text-blue-600 font-medium group-hover:underline">
                                        {r.tips.length} transaction{r.tips.length > 1 ? 's' : ''} • {isExpanded ? 'Hide breakdown' : 'Click to view details'}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-xs">
                                <div className="font-mono text-gray-700 font-medium">{r.email}</div>
                                {r.submitterEmails && r.submitterEmails.length > 0 && r.submitterEmails.some(e => e.toLowerCase() !== r.email.toLowerCase()) && (
                                  <div className="text-[10px] text-blue-600 font-sans font-semibold mt-0.5 flex items-center gap-1">
                                    <span className="text-gray-400 font-normal">Created by:</span>
                                    <span>{r.submitterEmails.filter(e => e.toLowerCase() !== r.email.toLowerCase()).join(', ')}</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 text-xs font-mono text-gray-500">{r.posId}</td>
                              <td className="px-6 py-4 text-right font-bold text-gray-900 text-sm">${r.totalAmount.toFixed(2)}</td>
                              <td className="px-6 py-4 text-xs font-medium text-gray-500">
                                {r.lastSubmissionDate ? r.lastSubmissionDate.toLocaleString() : 'N/A'}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${statusColor}`}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-xs">
                                {r.certification ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-200">
                                      <span>✓</span>
                                      <span className="italic">Signed: "{r.certification.typedSignature || 'Drawn'}"</span>
                                    </span>
                                    {r.certification.certifiedAt && (
                                      <span className="text-[9px] font-bold text-gray-400 pl-1">
                                        on {(() => {
                                          const d = typeof r.certification.certifiedAt.toDate === 'function' ? r.certification.certifiedAt.toDate() : new Date();
                                          return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        })()}
                                      </span>
                                    )}
                                    {(() => {
                                      const certDate = r.certification.certifiedAt && typeof r.certification.certifiedAt.toDate === 'function' ? r.certification.certifiedAt.toDate() : new Date();
                                      const deadlineReset = new Date(complianceDeadline.getFullYear(), complianceDeadline.getMonth(), complianceDeadline.getDate());
                                      const certReset = new Date(certDate.getFullYear(), certDate.getMonth(), certDate.getDate());
                                      if (certReset > deadlineReset) {
                                        return (
                                          <span className="text-[9px] font-bold text-amber-600 pl-1 flex items-center gap-0.5">
                                            ⚠️ Signed Late (Past Deadline)
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                ) : r.totalAmount >= 20 ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-100 animate-pulse">
                                    ⏳ Pending Sign-off
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium italic">
                                    Exempt (&lt;$20)
                                  </span>
                                )}
                              </td>
                            </tr>

                            {/* Detailed Itemized Transactions Drill-Down */}
                            {isExpanded && (
                              <tr>
                                <td colSpan={7} className="px-6 py-0 bg-blue-50/10 border-y border-blue-100">
                                  <div className="py-5 border-l-4 border-blue-600 ml-2 pl-6 animate-in slide-in-from-top-2 duration-300">
                                    <div className="flex items-center justify-between mb-4">
                                      <h4 className="text-[11px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                                        <FileText size={14} className="text-blue-600" />
                                        <span>Itemized Cash Tip Submissions for {r.name} ({r.email})</span>
                                      </h4>
                                      <span className="text-xs font-bold text-gray-500">
                                        Total: ${r.totalAmount.toFixed(2)} ({r.tips?.length || 0} entries)
                                      </span>
                                    </div>

                                    {r.tips && r.tips.length > 0 ? (
                                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {r.tips.map((tx: any, txIdx: number) => (
                                          <div key={tx.id || txIdx} className="p-4 bg-white rounded-xl border border-blue-100 shadow-sm space-y-2">
                                            <div className="flex justify-between items-start">
                                              <div>
                                                <p className="text-base font-black text-gray-900">${(tx.amount || 0).toFixed(2)}</p>
                                                <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[9px] font-black uppercase tracking-wider mt-0.5">
                                                  {tx.shiftName ? `${tx.shiftName} Shift` : 'Cash Tip'}
                                                </span>
                                              </div>
                                              <div className="text-right">
                                                <span className="text-xs font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded">
                                                  {tx.tipDate}
                                                </span>
                                              </div>
                                            </div>

                                            <div className="pt-2 border-t border-gray-100 space-y-1 text-xs">
                                              <div className="flex items-center justify-between text-gray-500">
                                                <span className="text-[10px] uppercase font-bold text-gray-400">Employee Email:</span>
                                                <span className="font-mono text-[11px] text-gray-700">{tx.email || r.email}</span>
                                              </div>
                                              <div className="flex items-center justify-between text-gray-500">
                                                <span className="text-[10px] uppercase font-bold text-gray-400">Created / Submitted by:</span>
                                                <span className="font-mono text-[11px] font-bold text-blue-600">
                                                  {tx.submittedBy || tx.email || r.email}
                                                </span>
                                              </div>
                                              {tx.payoutMethod && (
                                                <div className="flex items-center justify-between text-gray-500">
                                                  <span className="text-[10px] uppercase font-bold text-gray-400">Payout Method:</span>
                                                  <span className="text-gray-700 font-medium">{tx.payoutMethod}</span>
                                                </div>
                                              )}
                                              {tx.note && (
                                                <div className="pt-1 text-[11px] text-gray-500 italic bg-gray-50 p-1.5 rounded">
                                                  "{tx.note}"
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-400 italic">No individual cash tip submissions found for this period.</p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                    {complianceRecords.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-400 italic">
                          No staff found. Configure staff records to view compliance.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'reconciliation-log' ? (
        <ReconciliationLog tenantId={tenantId} />
      ) : (
        <PayrollPeriodManager tenantId={tenantId} />
      )}
    </div>
  );
};
